/**
 * External API functions for clicks, pages, and forms
 * Uses the new /api/external/v1 endpoints with proper formatting
 */

import { timeZones } from "@/lib/constants";
import {
  normalizeFormsBreakdownResponse,
  normalizeFormsDetailsResponse,
} from "@/lib/api/formsFields";

const PUBLIC_IS_PROD = import.meta.env.VITE_PUBLIC_IS_PROD;
const PUBLIC_IS_LOCAL = import.meta.env.VITE_PUBLIC_IS_LOCAL;

const EXTERNAL_API_URL =
  PUBLIC_IS_PROD === "TRUE"
    ? `https://app.humblytics.com/api/external/v1/`
    : PUBLIC_IS_LOCAL === "TRUE"
    ? `http://localhost:3000/api/external/v1/`
    : `https://humblytics.onrender.com/api/external/v1/`;

export const getTimezone = () => {
  const currentTimezone = localStorage.getItem("timezone");
  if (!currentTimezone) {
    const match = timeZones.find((timeZone) =>
      timeZone.utc.includes(Intl.DateTimeFormat().resolvedOptions().timeZone)
    );
    if (match) {
      return match.utc[0];
    }
  } else {
    const match = timeZones.find((timeZone) =>
      timeZone.utc.includes(currentTimezone)
    );
    if (match) {
      return match.utc[0];
    }
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Convert granularityPeriodPair to start/end dates and granularity
 * Handles:
 * - New format: "day_PAST_7_D" or "day_CUSTOM_2025-01-01_to_2025-01-31"
 * - Legacy format: "LAST_7_DAYS", "LAST_24_H", etc.
 */
/** Convert short frontend period (e.g. "7d", "30d") to the granularity_PERIOD format parseGranularityPeriod expects. */
const toExternalApiPeriod = (period: string): string => {
  if (period.includes(":")) {
    const [start, end] = period.split(":");
    const diffDays = Math.ceil((new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60 * 60 * 24));
    const granularity = diffDays <= 2 ? "hour" : diffDays > 90 ? "week" : "day";
    return `${granularity}_CUSTOM_${start}_to_${end}`;
  }
  const map: Record<string, string> = {
    "1d": "hour_PAST_24_H",
    "7d": "day_PAST_7_D",
    "30d": "day_PAST_30_D",
    "90d": "day_PAST_3_M",
    "180d": "week_PAST_6_M",
    "365d": "week_PAST_12_M",
    "12m": "month_PAST_12_M",
    ytd: "YEAR_TO_DATE",
    mtd: "MONTH_TO_DATE",
  };
  return map[period] || "day_PAST_7_D";
};

export const getGranularityForPeriod = (period: string): string =>
  parseGranularityPeriod(toExternalApiPeriod(period)).granularity;

/**
 * Resolve a granularity/period pair to the concrete `{ start, end }` ISO window
 * the external API is queried with. Exported so previous-period comparisons
 * (`usePreviousPeriodMetrics`) derive their window from the exact same range.
 */
export const parseGranularityPeriod = (granularityPeriodPair: string) => {
  const now = new Date();
  
  // Check if it's a custom date range: "YYYY-MM-DD_YYYY-MM-DD"
  if (/^\d{4}-\d{2}-\d{2}_\d{4}-\d{2}-\d{2}$/.test(granularityPeriodPair)) {
    const [startStr, endStr] = granularityPeriodPair.split("_");
    // Append T00:00:00 (no Z) so JS parses as local midnight, not UTC midnight
    const startDate = new Date(startStr + "T00:00:00");
    const endDate = new Date(endStr + "T23:59:59.999");
    return { start: startDate.toISOString(), end: endDate.toISOString(), granularity: "day" };
  }

  // Month-to-date and year-to-date
  if (granularityPeriodPair === "MONTH_TO_DATE") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: start.toISOString(), end: now.toISOString(), granularity: "day" };
  }
  if (granularityPeriodPair === "YEAR_TO_DATE") {
    const start = new Date(now.getFullYear(), 0, 1);
    return { start: start.toISOString(), end: now.toISOString(), granularity: "month" };
  }

  // Check if it's legacy format (LAST_X_Y without granularity prefix)
  if (granularityPeriodPair.startsWith("LAST_") || granularityPeriodPair.startsWith("PAST_")) {
    let start: Date;
    let granularity = "day"; // Default granularity

    switch (granularityPeriodPair) {
      case "LAST_24_H":
      case "PAST_24_H":
        start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        granularity = "hour";
        break;
      case "LAST_7_DAYS":
      case "PAST_7_D":
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        granularity = "day";
        break;
      case "LAST_30_DAYS":
      case "PAST_30_D":
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        granularity = "day";
        break;
      // "90d" maps to LAST_90_DAYS; without this case it fell through to the
      // 30-day default. Daily buckets, matching the day_PAST_3_M preset.
      case "LAST_90_DAYS":
        start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        granularity = "day";
        break;
      case "LAST_3_MONTHS":
      case "PAST_3_M":
        start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        granularity = "week";
        break;
      case "LAST_6_MONTHS":
      case "PAST_6_M":
        start = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
        granularity = "week";
        break;
      case "LAST_12_MONTHS":
      case "PAST_12_M":
        start = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        granularity = "month";
        break;
      default:
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        granularity = "day";
    }

    return {
      start: start.toISOString(),
      end: now.toISOString(),
      granularity,
    };
  }
  
  // New format with granularity prefix
  const parts = granularityPeriodPair.split("_");
  const granularity = parts[0];
  
  // Check if this is a custom date range format: granularity_CUSTOM_start_to_end
  if (parts[1] === "CUSTOM" && parts.length >= 4) {
    // Format: "day_CUSTOM_2025-01-01_to_2025-01-31"
    // parts = ["day", "CUSTOM", "2025-01-01", "to", "2025-01-31"]
    const startStr = parts[2];
    const endStr = parts[4];
    
    // Parse dates and set to start/end of day
    // Append T00:00:00 (no Z) so JS parses as local midnight, not UTC midnight
    const startDate = new Date(startStr + "T00:00:00");
    const endDate = new Date(endStr + "T23:59:59.999");

    return {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      granularity: granularity.toLowerCase(),
    };
  }
  
  // Handle preset periods with granularity prefix
  const period = parts.slice(1).join("_"); // Rejoin the rest as period
  let start: Date;

  switch (period) {
    case "PAST_24_H":
      start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      break;
    case "PAST_7_D":
      start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case "PAST_30_D":
      start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case "PAST_3_M":
      start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      break;
    case "PAST_6_M":
      start = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
      break;
    case "PAST_12_M":
      start = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
      break;
    default:
      start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  }

  return {
    start: start.toISOString(),
    end: now.toISOString(),
    granularity: granularity.toLowerCase(),
  };
};

// ============================================================================
// TRAFFIC API
// ============================================================================

interface BotTrafficResponse {
  meta: {
    property_id: string;
    start: string;
    end: string;
    timezone: string;
  };
  data: {
    page_views: number;
    unique_visitors: number;
    actions: number;
  };
}

const getBotTrafficSummary = async (
  granularityPeriod: string,
  propertyId: string,
  token: string,
  filters: Record<string, string> = {}
): Promise<BotTrafficResponse> => {
  const { start, end } = parseGranularityPeriod(granularityPeriod);
  const timezone = getTimezone();

  const params = new URLSearchParams({
    start,
    end,
    timezone,
    ...filters,
  });

  const endpoint = `properties/${propertyId}/traffic/bots?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch bot traffic: ${response.status}`);
  }

  return await response.json();
};

// Each bot session is attributed to exactly one reason (its highest-precedence
// match), so these counts sum to `bot.sessions` with no double counting.
export interface BotReasonBreakdown {
  verified_crawler: number;
  ua_pattern: number;
  datacenter_asn: number;
  behavioral: number;
}

export interface BotSource {
  label: string;
  kind: "asn" | "user_agent";
  sessions: number;
}

export interface BotTrafficComparisonResponse {
  meta: {
    property_id: string;
    start: string;
    end: string;
    timezone: string;
  };
  data: {
    bot: {
      page_views: number;
      unique_visitors: number;
      pct: number;
      // Optional — added by the pending backend contract; the UI renders these
      // when present and degrades to page-views/visitors otherwise.
      sessions?: number;
      reasons?: BotReasonBreakdown;
      top_sources?: BotSource[];
    };
    human: { page_views: number; unique_visitors: number; pct: number; sessions?: number };
    total: { page_views: number; unique_visitors?: number; sessions?: number };
  };
}

export const getBotTrafficComparison = async (
  granularityPeriod: string,
  propertyId: string,
  token: string,
  filters: Record<string, string> = {}
): Promise<BotTrafficComparisonResponse> => {
  const { start, end } = parseGranularityPeriod(granularityPeriod);
  const timezone = getTimezone();

  const params = new URLSearchParams({ start, end, timezone, ...filters });
  const endpoint = `properties/${propertyId}/traffic/bots-comparison?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch bot traffic comparison: ${response.status}`);
  }

  return await response.json();
};

interface TrafficTrendBucket {
  bucket_start: string;
  page_views: number;
  unique_visitors: number;
}

interface TrafficSummary {
  page_views: number;
  unique_visitors: number;
  total_sessions: number;
  bounce_count: number;
  single_page_sessions: number;
  total_session_time_ms: number;
  form_submissions: number;
  clicks: number;
  avg_session_duration: number;
  bounce_rate: number;
  single_page_bounce_rate: number;
}

interface TrafficTrendsResponse {
  meta: {
    property_id: string;
    start: string;
    end: string;
    timezone: string;
    granularity: string;
    bucket_count: number;
  };
  data: TrafficTrendBucket[];
}

interface TrafficSummaryResponse {
  meta: {
    property_id: string;
    start: string;
    end: string;
    timezone: string;
  };
  data: TrafficSummary;
}

/**
 * Get traffic trends (timeseries data)
 */
export const getTrafficTrends = async (
  granularityPeriod: string,
  propertyId: string,
  token: string,
  filters: Record<string, string> = {}
): Promise<TrafficTrendsResponse> => {
  const { start, end, granularity } = parseGranularityPeriod(granularityPeriod);
  const timezone = getTimezone();

  const params = new URLSearchParams({
    start,
    end,
    timezone,
    granularity,
    ...filters,
  });

  const endpoint = `properties/${propertyId}/traffic/trends?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch traffic trends: ${response.status}`);
  }

  return await response.json();
};

/**
 * Get traffic summary (aggregate metrics)
 */
export const getTrafficSummary = async (
  granularityPeriod: string,
  propertyId: string,
  token: string,
  filters: Record<string, string> = {}
): Promise<TrafficSummaryResponse> => {
  const { start, end } = parseGranularityPeriod(granularityPeriod);
  const timezone = getTimezone();

  const params = new URLSearchParams({
    start,
    end,
    timezone,
    ...filters,
  });

  const endpoint = `properties/${propertyId}/traffic/summary?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch traffic summary: ${response.status}`);
  }

  return await response.json();
};

/**
 * Get realtime traffic
 */
export const getTrafficRealtime = async (
  propertyId: string,
  token: string
): Promise<any> => {
  const endpoint = `properties/${propertyId}/traffic/realtime`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch realtime traffic: ${response.status}`);
  }

  return await response.json();
};

/**
 * Get traffic breakdown (devices, locations, referrers, etc.)
 */
export const getTrafficBreakdown = async (
  granularityPeriod: string,
  propertyId: string,
  token: string,
  limit: number = 50,
  filters: Record<string, string> = {}
): Promise<any> => {
  const { start, end } = parseGranularityPeriod(granularityPeriod);
  const timezone = getTimezone();

  const params = new URLSearchParams({
    start,
    end,
    timezone,
    limit: limit.toString(),
    ...filters,
  });

  const endpoint = `properties/${propertyId}/traffic/breakdown?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch traffic breakdown: ${response.status}`);
  }

  return await response.json();
};

/**
 * Get entry and exit pages
 */
export const getEntryExitPages = async (
  granularityPeriod: string,
  propertyId: string,
  token: string,
  limit: number = 50,
  filters: Record<string, string> = {}
): Promise<any> => {
  const { start, end } = parseGranularityPeriod(granularityPeriod);
  const timezone = getTimezone();

  const params = new URLSearchParams({
    start,
    end,
    timezone,
    limit: limit.toString(),
    ...filters,
  });

  const endpoint = `properties/${propertyId}/traffic/entry-exit-pages?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch entry/exit pages: ${response.status}`);
  }

  return await response.json();
};

// ============================================================================
// FILTER OPTIONS API
// ============================================================================

// In-flight request cache: deduplicates concurrent calls with the same key.
// Callers sharing a key await the same promise — no duplicate network requests.
// Cleared automatically when the promise settles.
const filterOptionsInflight = new Map<string, Promise<any>>();

export const getFilterOptions = (
  period: string,
  propertyIds: string[],
  token: string
): Promise<any> => {
  const { start, end } = parseGranularityPeriod(toExternalApiPeriod(period));
  const timezone = getTimezone();
  const key = `${propertyIds.sort().join(",")}|${start}|${end}`;

  if (filterOptionsInflight.has(key)) {
    return filterOptionsInflight.get(key)!;
  }

  const params = new URLSearchParams({ start, end, timezone });

  const fetchPromise = (async () => {
    // Single property — use the per-property endpoint
    if (propertyIds.length === 1) {
      const response = await fetch(
        `${EXTERNAL_API_URL}properties/${propertyIds[0]}/filters?${params}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          credentials: "include",
        }
      );
      if (!response.ok) throw new Error(`Failed to fetch filter options: ${response.status}`);
      const json = await response.json();
      return json.data;
    }

    // Multiple properties — fetch in parallel and merge
    const results = await Promise.all(
      propertyIds.map(async (id) => {
        const response = await fetch(
          `${EXTERNAL_API_URL}properties/${id}/filters?${params}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            credentials: "include",
          }
        );
        if (!response.ok) return null;
        const json = await response.json();
        return json.data;
      })
    );

    const mergeArr = (extract: (r: any) => string[]): string[] =>
      [...new Set(results.filter(Boolean).flatMap(extract))];

    return {
      pages: {
        all: mergeArr((r) => r.pages?.all || []),
        entry: mergeArr((r) => r.pages?.all || []),
        exit: mergeArr((r) => r.pages?.all || []),
      },
      devices: {
        os: mergeArr((r) => r.devices?.os || []),
        types: [...new Set(results.filter(Boolean).flatMap((r: any) => r.devices?.types || []))],
      },
      browsers: mergeArr((r) => r.browsers || []),
      countries: mergeArr((r) => r.countries || []),
      regions: mergeArr((r) => r.regions || []),
      referrers: mergeArr((r) => r.referrers || []),
      utm: {
        sources: mergeArr((r) => r.utm?.sources || []),
        mediums: mergeArr((r) => r.utm?.mediums || []),
        campaigns: mergeArr((r) => r.utm?.campaigns || []),
        contents: mergeArr((r) => r.utm?.contents || []),
        terms: mergeArr((r) => r.utm?.terms || []),
        ids: mergeArr((r) => r.utm?.ids || []),
      },
    };
  })().finally(() => filterOptionsInflight.delete(key));

  filterOptionsInflight.set(key, fetchPromise);
  return fetchPromise;
};

// ============================================================================
// CLICKS API
// ============================================================================

export interface ClickTarget {
  target: string;
  clicks: number;
  share: number;
}

export interface ClicksBreakdownPage {
  page: string;
  page_group: string;
  total_clicks: number;
  unique_sessions: number;
  top_targets: ClickTarget[];
}

export interface ClicksGroup {
  pages: number;
  total_clicks: number;
  unique_sessions: number;
}

export interface ClickTrendBucket {
  bucket_start: string;
  clicks: number;
  unique_sessions: number;
}

export interface ClicksBreakdownResponse {
  meta: {
    property_id?: string;
    property_ids?: string[];
    start: string;
    end: string;
    timezone: string;
    granularity: string;
    limit: number;
    offset?: number;
    total_count?: number;
    bucket_count: number;
  };
  data: ClicksBreakdownPage[];
  trend: ClickTrendBucket[];
  summary: {
    total_clicks: number;
    unique_sessions: number;
    pages_with_clicks: number;
    groups: Record<string, ClicksGroup>;
  };
}

interface UTMBreakdown {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
  utm_id: string;
  clicks: number;
  share: number;
}

export interface ClickTrendBucket {
  bucket_start: string;
  clicks: number;
}

export interface ClickDetails {
  target: string;
  secondary: string | null;
  clicks: number;
  unique_sessions: number;
  most_recent: string;
  utm_breakdown: UTMBreakdown[];
  trend: ClickTrendBucket[];
}

export interface ClicksDetailsResponse {
  meta: {
    property_id: string;
    page: string;
    start: string;
    end: string;
    timezone: string;
    granularity: string;
    limit: number;
    offset: number;
    total_count: number;
    bucket_count: number;
  };
  data: {
    clicks: ClickDetails[];
  };
  summary: {
    page: string;
    total_clicks: number;
    unique_sessions: number;
    page_views: number;
    unique_visitors: number;
    click_through_rate: number;
  };
}

/**
 * Get clicks breakdown across all pages
 */
export const getClicksBreakdown = async (
  granularityPeriod: string,
  propertyId: string,
  token: string,
  limit: number = 50,
  filters: Record<string, string> = {}
): Promise<ClicksBreakdownResponse> => {
  const { start, end, granularity } = parseGranularityPeriod(granularityPeriod);
  const timezone = getTimezone();

  const params = new URLSearchParams({
    start,
    end,
    timezone,
    granularity,
    limit: limit.toString(),
    ...filters,
  });

  const endpoint = `properties/${propertyId}/clicks/breakdown?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch clicks breakdown: ${response.status}`);
  }

  return await response.json();
};

/**
 * Get detailed clicks for a specific page
 */
export const getClicksDetails = async (
  page: string,
  granularityPeriod: string,
  propertyId: string,
  token: string,
  limit: number = 20,
  offset: number = 0,
  filters: Record<string, string> = {}
): Promise<ClicksDetailsResponse> => {
  const { start, end, granularity } = parseGranularityPeriod(granularityPeriod);
  const timezone = getTimezone();

  const params = new URLSearchParams({
    page,
    start,
    end,
    timezone,
    granularity,
    limit: limit.toString(),
    offset: offset.toString(),
    ...filters,
  });

  const endpoint = `properties/${propertyId}/clicks/details?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch clicks details: ${response.status}`);
  }

  return await response.json();
};

// ============================================================================
// PAGES API
// ============================================================================

export interface TopSource {
  utm_source: string;
  utm_medium: string;
  sessions: number;
  share: number;
}

export interface PageBreakdown {
  page: string;
  page_group: string;
  page_views: number;
  unique_visitors: number;
  avg_session_length: number;
  avg_scroll_percent: number;
  bounce_rate: number;
  top_sources: TopSource[];
}

export interface PageGroup {
  pages: number;
  page_views: number;
  unique_visitors: number;
}

export interface PageTrendBucket {
  bucket_start: string;
  page_views: number;
  unique_visitors: number;
}

export interface PagesBreakdownResponse {
  meta: {
    property_id?: string;
    property_ids?: string[];
    start: string;
    end: string;
    timezone: string;
    granularity: string;
    limit: number;
    offset?: number;
    total_count?: number;
    bucket_count: number;
  };
  data: PageBreakdown[];
  trend: PageTrendBucket[];
  summary: {
    total_pages: number;
    total_page_views: number;
    total_unique_visitors: number;
    groups: Record<string, PageGroup>;
  };
}

export interface PageUTMBreakdown {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
  utm_id: string;
  referer_group: string;
  page_views: number;
  unique_visitors: number;
  share: number;
}

export interface PageTrendBucket {
  bucket_start: string;
  page_views: number;
  unique_visitors: number;
}

export interface DeviceBreakdown {
  device: "mobile" | "desktop" | "unknown";
  page_views: number;
  unique_visitors: number;
  share: number;
}

export interface CountryBreakdown {
  country: string;
  page_views: number;
  unique_visitors: number;
  share: number;
}

export interface PagesDetailsResponse {
  meta: {
    property_id: string;
    page: string;
    start: string;
    end: string;
    timezone: string;
    granularity: string;
    utm_limit: number;
    bucket_count: number;
  };
  data: {
    metrics: {
      page_views: number;
      unique_visitors: number;
      avg_session_length: number;
      avg_scroll_percent: number;
      bounce_rate: number;
    };
    utm_breakdown: PageUTMBreakdown[];
    trend: PageTrendBucket[];
    device_breakdown: DeviceBreakdown[];
    country_breakdown: CountryBreakdown[];
  };
  summary: {
    page: string;
    total_page_views: number;
    total_unique_visitors: number;
  };
}

/**
 * Get pages breakdown across all pages
 */
export const getPagesBreakdown = async (
  granularityPeriod: string,
  propertyId: string,
  token: string,
  limit: number = 50,
  filters: Record<string, string> = {}
): Promise<PagesBreakdownResponse> => {
  const { start, end, granularity } = parseGranularityPeriod(granularityPeriod);
  const timezone = getTimezone();

  const params = new URLSearchParams({
    start,
    end,
    timezone,
    granularity,
    limit: limit.toString(),
    ...filters,
  });

  const endpoint = `properties/${propertyId}/pages/breakdown?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch pages breakdown: ${response.status}`);
  }

  return await response.json();
};

/**
 * Get aggregated pages breakdown across multiple properties
 */
export const getAggregatePagesBreakdown = async (
  propertyIds: string[],
  granularityPeriod: string,
  token: string,
  limit: number = 50,
  sharePropertyId?: string,
  search?: string,
  offset: number = 0,
  filters: Record<string, string> = {},
): Promise<PagesBreakdownResponse> => {
  const { start, end, granularity } = parseGranularityPeriod(granularityPeriod);
  const timezone = getTimezone();

  const params = new URLSearchParams({ start, end, timezone, granularity, limit: limit.toString(), offset: offset.toString(), ...(search ? { search } : {}), ...filters });
  const endpoint = `properties/aggregate/pages/breakdown?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
    body: JSON.stringify({ propertyIds, ...(sharePropertyId ? { sharePropertyId } : {}) }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch aggregate pages breakdown: ${response.status}`);
  }

  return await response.json();
};

/**
 * Get detailed analytics for a specific page
 */
export const getPagesDetails = async (
  page: string,
  granularityPeriod: string,
  propertyId: string,
  token: string,
  utmLimit: number = 20,
  filters: Record<string, string> = {}
): Promise<PagesDetailsResponse> => {
  const { start, end, granularity } = parseGranularityPeriod(granularityPeriod);
  const timezone = getTimezone();

  const params = new URLSearchParams({
    page,
    start,
    end,
    timezone,
    granularity,
    utm_limit: utmLimit.toString(),
    ...filters,
  });

  const endpoint = `properties/${propertyId}/pages/details?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch pages details: ${response.status}`);
  }

  return await response.json();
};

// ============================================================================
// FORMS API
// ============================================================================

export interface TopForm {
  target: string;
  submissions: number;
  share: number;
  source?: string;
}

export interface FormsBreakdownPage {
  page: string;
  page_group: string;
  total_submissions: number;
  /** Distinct sessions that submitted — public docs name. */
  unique_submissions: number;
  /** Live API alias for unique_submissions (same value after normalize). */
  unique_sessions: number;
  top_forms: TopForm[];
}

export interface FormsGroup {
  pages: number;
  total_submissions: number;
  unique_submissions: number;
  unique_sessions: number;
  total_unique_submissions?: number;
}

export interface FormTrendBucket {
  bucket_start: string;
  submissions: number;
  unique_submissions: number;
  unique_sessions: number;
}

export interface FormsBreakdownResponse {
  meta: {
    property_id?: string;
    property_ids?: string[];
    start: string;
    end: string;
    timezone: string;
    granularity: string;
    limit: number;
    offset?: number;
    total_count?: number;
    bucket_count: number;
  };
  data: FormsBreakdownPage[];
  trend: FormTrendBucket[];
  summary: {
    total_submissions: number;
    /** Distinct sessions that submitted — public docs name. */
    total_unique_submissions: number;
    unique_submissions: number;
    /** Live API alias for total_unique_submissions (same value after normalize). */
    unique_sessions: number;
    pages_with_forms: number;
    groups: Record<string, FormsGroup>;
  };
}

interface FormUTMBreakdown {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
  utm_id: string;
  referer_group: string;
  submissions: number;
  share: number;
}

export interface FormDetails {
  target: string;
  submissions: number;
  unique_submissions: number;
  unique_sessions: number;
  most_recent: string;
  utm_breakdown: FormUTMBreakdown[];
  trend: FormTrendBucket[];
}

export interface FormsDetailsResponse {
  meta: {
    property_id: string;
    page: string;
    start: string;
    end: string;
    timezone: string;
    granularity: string;
    limit: number;
    bucket_count: number;
  };
  data: {
    forms: FormDetails[];
  };
  summary: {
    page: string;
    total_submissions: number;
    total_unique_submissions: number;
    unique_submissions: number;
    unique_sessions: number;
    page_views: number;
    unique_visitors: number;
    conversion_rate: number;
  };
}

/**
 * Get forms breakdown across all pages
 */
export const getFormsBreakdown = async (
  granularityPeriod: string,
  propertyId: string,
  token: string,
  limit: number = 50,
  source?: string,
  filters: Record<string, string> = {}
): Promise<FormsBreakdownResponse> => {
  const { start, end, granularity } = parseGranularityPeriod(granularityPeriod);
  const timezone = getTimezone();

  const params = new URLSearchParams({
    start,
    end,
    timezone,
    granularity,
    limit: limit.toString(),
    ...filters,
  });

  if (source && source !== 'all') {
    params.set('source', source);
  }

  const endpoint = `properties/${propertyId}/forms/breakdown?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch forms breakdown: ${response.status}`);
  }

  return normalizeFormsBreakdownResponse(await response.json());
};

/**
 * Get detailed form submissions for a specific page
 */
export const getFormsDetails = async (
  page: string,
  granularityPeriod: string,
  propertyId: string,
  token: string,
  limit: number = 20,
  source?: string,
  filters: Record<string, string> = {}
): Promise<FormsDetailsResponse> => {
  const { start, end, granularity } = parseGranularityPeriod(granularityPeriod);
  const timezone = getTimezone();

  const params = new URLSearchParams({
    page,
    start,
    end,
    timezone,
    granularity,
    limit: limit.toString(),
    ...filters,
  });

  if (source && source !== 'all') {
    params.set('source', source);
  }

  const endpoint = `properties/${propertyId}/forms/details?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch forms details: ${response.status}`);
  }

  return normalizeFormsDetailsResponse(await response.json());
};

// ============================================================================
// MULTI-PROPERTY AGGREGATE API
// These endpoints aggregate data across multiple properties in a workspace
// ============================================================================

interface AggregateTrafficTrendsResponse {
  meta: {
    property_ids: string[];
    property_count: number;
    start: string;
    end: string;
    timezone: string;
    granularity: string;
    bucket_count: number;
  };
  data: TrafficTrendBucket[];
  summary: {
    page_views: number;
    unique_visitors: number;
  };
}

interface AggregateTrafficSummaryResponse {
  meta: {
    property_ids: string[];
    property_count: number;
    start: string;
    end: string;
    timezone: string;
  };
  data: TrafficSummary & {
    avg_session_duration: number;
    bounce_rate: number;
    single_page_bounce_rate: number;
  };
}

interface AggregateTrafficBreakdownResponse {
  meta: {
    property_ids: string[];
    property_count: number;
    start: string;
    end: string;
    timezone: string;
    limit: number;
  };
  summary: {
    totals: {
      sessions: number;
      page_views: number;
    };
    devices: {
      mobile: { sessions: number; page_views: number; share: number };
      desktop: { sessions: number; page_views: number; share: number };
    };
  };
  data: {
    utm: Array<{
      source: string;
      medium: string;
      campaign: string;
      sessions: number;
      page_views: number;
      share: number;
    }>;
    countries: Array<{
      country: string;
      sessions: number;
      page_views: number;
      share: number;
    }>;
    cities?: Array<{
      city: string;
      country: string;
      sessions: number;
      page_views: number;
      share: number;
    }>;
    regions?: Array<{
      region: string;
      country: string;
      sessions: number;
      page_views: number;
      share: number;
    }>;
    devices: Array<{
      device: string;
      sessions: number;
      page_views: number;
      share: number;
    }>;
    landing_pages: Array<{
      path: string;
      sessions: number;
      page_views: number;
      share: number;
    }>;
    referrers: Array<{
      referrer: string;
      sessions: number;
      page_views: number;
      share: number;
    }>;
    browsers: Array<{
      browser: string;
      sessions: number;
      page_views: number;
      share: number;
    }>;
    os: Array<{
      os: string;
      sessions: number;
      page_views: number;
      share: number;
    }>;
  };
}

interface AggregateRealtimeResponse {
  meta: {
    property_ids: string[];
    property_count: number;
  };
  data: {
    active_visitors: number;
    live_feed?: Array<{
      time: string;
      page: string;
      referrer?: string;
      country?: string;
    }>;
    sessions?: Array<{
      session_id: string;
      view_id: string;
      page: string;
      time: string;
      client_os?: string;
      client_browser?: string;
      country?: string;
      utm_source?: string;
    }>;
    metrics?: Array<{
      event_id: string;
      time: string;
      action: string;
      target?: string;
      secondary?: string;
      x?: number;
      y?: number;
      page?: string;
      session_id?: string;
      view_id?: string;
      client_os?: string;
      client_browser?: string;
      device_group?: string;
      referer_group?: string | null;
      country?: string;
      utm_source?: string | null;
      utm_medium?: string | null;
      utm_campaign?: string | null;
    }>;
  };
}

interface AggregateEntryExitPagesResponse {
  meta: {
    property_ids: string[];
    property_count: number;
    start: string;
    end: string;
    limit: number;
  };
  data: {
    entry_pages: Array<{ page: string; count: number }>;
    exit_pages: Array<{ page: string; count: number }>;
  };
}

/**
 * Get aggregated traffic trends across multiple properties
 */
export const getAggregateTrafficTrends = async (
  propertyIds: string[],
  granularityPeriod: string,
  token: string,
  sharePropertyId?: string,
  filters: Record<string, string> = {},
): Promise<AggregateTrafficTrendsResponse> => {
  const { start, end, granularity } = parseGranularityPeriod(granularityPeriod);
  return getAggregateTrafficTrendsForRange(propertyIds, { start, end, granularity }, token, sharePropertyId, filters);
};

/**
 * Aggregated traffic trends for an explicit ISO window. Used to draw the
 * previous period on the Overview chart (same buckets, earlier window).
 */
export const getAggregateTrafficTrendsForRange = async (
  propertyIds: string[],
  range: { start: string; end: string; granularity: string },
  token: string,
  sharePropertyId?: string,
  filters: Record<string, string> = {},
): Promise<AggregateTrafficTrendsResponse> => {
  const { start, end, granularity } = range;
  const timezone = getTimezone();

  const params = new URLSearchParams({
    start,
    end,
    timezone,
    granularity,
    ...filters,
  });

  const endpoint = `properties/aggregate/traffic/trends?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
    body: JSON.stringify({ propertyIds, ...(sharePropertyId ? { sharePropertyId } : {}) }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch aggregate traffic trends: ${response.status}`);
  }

  return await response.json();
};

/**
 * Get aggregated traffic summary across multiple properties
 */
export const getAggregateTrafficSummary = async (
  propertyIds: string[],
  granularityPeriod: string,
  token: string,
  sharePropertyId?: string,
  filters: Record<string, string> = {},
): Promise<AggregateTrafficSummaryResponse> => {
  const { start, end } = parseGranularityPeriod(granularityPeriod);
  return getAggregateTrafficSummaryForRange(propertyIds, { start, end }, token, sharePropertyId, filters);
};

/**
 * Aggregated traffic summary for an explicit ISO window. The endpoint takes
 * arbitrary `start`/`end` instants, which is what makes true previous-period
 * comparisons possible without a backend change.
 */
export const getAggregateTrafficSummaryForRange = async (
  propertyIds: string[],
  range: { start: string; end: string },
  token: string,
  sharePropertyId?: string,
  filters: Record<string, string> = {},
): Promise<AggregateTrafficSummaryResponse> => {
  const { start, end } = range;
  const timezone = getTimezone();

  const params = new URLSearchParams({
    start,
    end,
    timezone,
    ...filters,
  });

  const endpoint = `properties/aggregate/traffic/summary?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
    body: JSON.stringify({ propertyIds, ...(sharePropertyId ? { sharePropertyId } : {}) }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch aggregate traffic summary: ${response.status}`);
  }

  return await response.json();
};

/**
 * Get aggregated traffic breakdown across multiple properties
 */
export const getAggregateTrafficBreakdown = async (
  propertyIds: string[],
  granularityPeriod: string,
  token: string,
  limit: number = 50,
  sharePropertyId?: string,
  filters: Record<string, string> = {},
): Promise<AggregateTrafficBreakdownResponse> => {
  const { start, end } = parseGranularityPeriod(granularityPeriod);
  const timezone = getTimezone();

  const params = new URLSearchParams({
    start,
    end,
    timezone,
    limit: limit.toString(),
    ...filters,
  });

  const endpoint = `properties/aggregate/traffic/breakdown?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
    body: JSON.stringify({ propertyIds, ...(sharePropertyId ? { sharePropertyId } : {}) }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch aggregate traffic breakdown: ${response.status}`);
  }

  return await response.json();
};

/**
 * Get aggregated realtime traffic across multiple properties
 */
export const getAggregateTrafficRealtime = async (
  propertyIds: string[],
  token: string,
  sharePropertyId?: string
): Promise<AggregateRealtimeResponse> => {
  const endpoint = `properties/aggregate/traffic/realtime`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
    body: JSON.stringify({ propertyIds, ...(sharePropertyId ? { sharePropertyId } : {}) }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch aggregate realtime traffic: ${response.status}`);
  }

  return await response.json();
};

/**
 * Get aggregated entry/exit pages across multiple properties
 */
export const getAggregateEntryExitPages = async (
  propertyIds: string[],
  granularityPeriod: string,
  token: string,
  limit: number = 50,
  sharePropertyId?: string,
  filters: Record<string, string> = {},
): Promise<AggregateEntryExitPagesResponse> => {
  const { start, end } = parseGranularityPeriod(granularityPeriod);
  const timezone = getTimezone();

  const params = new URLSearchParams({
    start,
    end,
    timezone,
    limit: limit.toString(),
    ...filters,
  });

  const endpoint = `properties/aggregate/traffic/entry-exit-pages?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
    body: JSON.stringify({ propertyIds, ...(sharePropertyId ? { sharePropertyId } : {}) }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch aggregate entry/exit pages: ${response.status}`);
  }

  return await response.json();
};

/**
 * Get aggregated clicks breakdown across multiple properties
 */
export const getAggregateClicksBreakdown = async (
  propertyIds: string[],
  granularityPeriod: string,
  token: string,
  limit: number = 50,
  sharePropertyId?: string,
  filters: Record<string, string> = {},
  search?: string,
  offset: number = 0,
): Promise<ClicksBreakdownResponse> => {
  const { start, end, granularity } = parseGranularityPeriod(granularityPeriod);
  const timezone = getTimezone();

  const params = new URLSearchParams({
    start,
    end,
    timezone,
    granularity,
    limit: limit.toString(),
    offset: offset.toString(),
    ...filters,
    ...(search ? { search } : {}),
  });

  const endpoint = `properties/aggregate/clicks/breakdown?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
    body: JSON.stringify({ propertyIds, ...(sharePropertyId ? { sharePropertyId } : {}) }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch aggregate clicks breakdown: ${response.status}`);
  }

  return await response.json();
};

/**
 * Get aggregated forms breakdown across multiple properties
 */
export const getAggregateFormsBreakdown = async (
  propertyIds: string[],
  granularityPeriod: string,
  token: string,
  limit: number = 50,
  sharePropertyId?: string,
  filters: Record<string, string> = {},
  search?: string,
  offset: number = 0,
): Promise<FormsBreakdownResponse> => {
  const { start, end, granularity } = parseGranularityPeriod(granularityPeriod);
  const timezone = getTimezone();

  const params = new URLSearchParams({
    start,
    end,
    timezone,
    granularity,
    limit: limit.toString(),
    offset: offset.toString(),
    ...filters,
    ...(search ? { search } : {}),
  });

  const endpoint = `properties/aggregate/forms/breakdown?${params.toString()}`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
    body: JSON.stringify({ propertyIds, ...(sharePropertyId ? { sharePropertyId } : {}) }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch aggregate forms breakdown: ${response.status}`);
  }

  return normalizeFormsBreakdownResponse(await response.json());
};

// ---- Property attribution (first-touch / last-touch) ----

export interface PropertyAttributionRow {
  source: string
  medium: string
  campaign: string
  referrer_group: string
  conversions: number
}

interface PropertyAttributionMeta {
  property_id: string
  start: string
  end: string
  conversion_event: string
  model: 'first_touch' | 'last_touch'
}

interface PropertyAttributionResponse {
  meta: PropertyAttributionMeta
  data: PropertyAttributionRow[]
}

export const getPropertyAttributionFirstTouch = async (
  token: string,
  propertyId: string,
  start: string,
  end: string,
  timezone?: string,
  conversionEvent?: string
): Promise<PropertyAttributionResponse> => {
  const params = new URLSearchParams({ start, end })
  if (timezone) params.set('timezone', timezone)
  if (conversionEvent) params.set('conversion_event', conversionEvent)
  const response = await fetch(
    `${EXTERNAL_API_URL}properties/${propertyId}/attribution/first-touch?${params}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
    }
  );
  if (!response.ok) throw new Error(`attribution/first-touch HTTP ${response.status}`);
  return response.json();
};

export const getPropertyAttributionLastTouch = async (
  token: string,
  propertyId: string,
  start: string,
  end: string,
  timezone?: string,
  conversionEvent?: string
): Promise<PropertyAttributionResponse> => {
  const params = new URLSearchParams({ start, end })
  if (timezone) params.set('timezone', timezone)
  if (conversionEvent) params.set('conversion_event', conversionEvent)
  const response = await fetch(
    `${EXTERNAL_API_URL}properties/${propertyId}/attribution/last-touch?${params}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
    }
  );
  if (!response.ok) throw new Error(`attribution/last-touch HTTP ${response.status}`);
  return response.json();
};
